import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  DeclinedReasonQueryParameters,
  ServiceGroupQueryParameters,
  ServiceGroupRateQueryParameters,
  ServiceQueryParameters,
  VehicleMaintenanceScheduleQueryParameters,
  VehicleMaintenanceScheduleServiceDueQueryParameters,
  VehicleQueryParameters,
  VehicleTemplateDetailQueryParameters,
} from '@quorum/models/xs-query';
import { Actions, createEffect } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { Employee } from '@quorum/models/xs-resource';
import { fromAppointments, fromQuotes, fromWorkOrders } from '@quorum/xsr-fixed-operations/state';
import {
  fromDeclinedReasons,
  fromServiceGroupRates,
  fromServiceGroups,
  fromServices,
} from '@quorum/xsr-labour-operations/state';
import {
  MenuComponent,
  NoToolbarMenuComponent,
  QuoteMenuComponent,
  MenuShellComponent,
} from '@quorum/xsr-maintenance-menu-ng-ui';
import { fromVehicles } from '@quorum/xsr-vehicle/state';
import { of } from 'rxjs';
import { filter, mergeAll, switchMap } from 'rxjs/operators';
import * as fromAcceptedServices from './accepted-services/accepted-services.actions';
import * as fromDeclinedServices from './declined-services/declined-services.actions';
import * as fromMaintenanceschedule from './maintenance-schedule/maintenance-schedule.actions';
// eslint-disable-next-line max-len
import * as fromPendingAcceptedServices from './pending-accepted-services/pending-accepted-services.actions';
// eslint-disable-next-line max-len
import * as fromPendingDeclinedServices from './pending-declined-services/pending-declined-services.actions';
import * as fromServicesDue from './services-due/services-due.actions';
import * as fromTemplateDetail from './template-detail/template-detail.actions';
import * as fromVehicleMaintenance from './vehicle-maintenance.actions';
import { VehicleMaintenanceState } from './vehicle-maintenance.interfaces';

@Injectable()
export class VehicleMaintenanceEffects {
  loadMenuFromRouter = createEffect(() =>
    this.dataPersistence.navigation(MenuComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee !== null),
          switchMap((employee: Employee) => {
            let params = new VehicleMaintenanceScheduleQueryParameters({ vehicleId: a.params.id });
            let params2 = new VehicleMaintenanceScheduleServiceDueQueryParameters({ vehicleId: a.params.id });
            let actions: Action[] = [
              new fromVehicles.GetVehiclesFromServer(new VehicleQueryParameters({ id: a.params.id })),
              new fromMaintenanceschedule.GetMaintenanceSchedulesFromServer(params),
              new fromServicesDue.GetServicesDueFromServer(params2),
              new fromServiceGroups.GetServiceGroupsFromServer(
                new ServiceGroupQueryParameters({
                  rooftopId: employee.rooftopId.toString() + ',-1',
                  page: 0,
                  pageSize: 2000,
                })
              ),
              new fromServices.GetServicesFromServer(new ServiceQueryParameters({ page: 0, pageSize: 2000 })),
              new fromServiceGroupRates.GetServiceGroupRatesFromServer(
                new ServiceGroupRateQueryParameters({
                  effectiveDate: new Date(),
                  expiryDate: new Date(),
                  page: 0,
                  pageSize: 2000,
                })
              ),
              new fromTemplateDetail.GetTemplateDetailsFromServer({
                params: new VehicleTemplateDetailQueryParameters(),
                vehicleId: a.params.id,
              }),
              new fromDeclinedReasons.GetDeclinedReasonsFromServer(
                new DeclinedReasonQueryParameters({ rooftopId: employee.rooftopId.toString() + ',-1' })
              ),
              new fromQuotes.GetQuotesFromServer({ vehicleId: a.params.id }),
            ];
            let transactionAction: any;
            let loadTransaction: any = null;
            // XS sends 0 for "no work order" / "no appointment"; only a positive id is a real transaction.
            if (Number(a.queryParams.workOrderId) > 0) {
              actions.push(
                new fromVehicleMaintenance.SetTransactionFromQueryParams({
                  source: 'workOrder',
                  id: a.queryParams.workOrderId,
                })
              );
              actions.push(
                new fromWorkOrders.GetWorkOrderFromServer({
                  id: a.queryParams.workOrderId,
                  query: { embed: 'cases,declinedMpviCode' },
                })
              );
            } else if (Number(a.queryParams.appointmentId) > 0) {
              actions.push(
                new fromVehicleMaintenance.SetTransactionFromQueryParams({
                  source: 'appointmentId',
                  id: a.queryParams.appointmentId,
                })
              );
              actions.push(
                new fromAppointments.GetAppointmentFromServer({
                  id: a.queryParams.appointmentId,
                  query: { embed: 'cases,serviceAppointmentDeclinedMpviCode' },
                })
              );
            } else {
              actions.push(
                (transactionAction = new fromVehicleMaintenance.SetTransactionFromQueryParams({
                  source: 'vehicle',
                  id: a.params.id,
                }))
              );
            }
            return of(actions).pipe(mergeAll());
          })
        );

        //return new fromConversations.GetConversationFromStateServer(a.params.id);
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadLazyMenuFromRouter = createEffect(() =>
    this.dataPersistence.navigation(MenuShellComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        if (a.routeConfig.path === 'vehicle/:id') {
          return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
            filter((employee) => employee !== null),
            switchMap((employee: Employee) => {
              let params = new VehicleMaintenanceScheduleQueryParameters({ vehicleId: a.params.id });
              let params2 = new VehicleMaintenanceScheduleServiceDueQueryParameters({ vehicleId: a.params.id });
              let actions: Action[] = [
                new fromVehicles.GetVehiclesFromServer(new VehicleQueryParameters({ id: a.params.id })),
                new fromMaintenanceschedule.GetMaintenanceSchedulesFromServer(params),
                new fromServicesDue.GetServicesDueFromServer(params2),
                new fromServiceGroups.GetServiceGroupsFromServer(
                  new ServiceGroupQueryParameters({
                    rooftopId: employee.rooftopId.toString() + ',-1',
                    page: 0,
                    pageSize: 2000,
                  })
                ),
                new fromServices.GetServicesFromServer(new ServiceQueryParameters({ page: 0, pageSize: 2000 })),
                new fromServiceGroupRates.GetServiceGroupRatesFromServer(
                  new ServiceGroupRateQueryParameters({
                    effectiveDate: new Date(),
                    expiryDate: new Date(),
                    page: 0,
                    pageSize: 2000,
                  })
                ),
                new fromTemplateDetail.GetTemplateDetailsFromServer({
                  params: new VehicleTemplateDetailQueryParameters(),
                  vehicleId: a.params.id,
                }),
                new fromDeclinedReasons.GetDeclinedReasonsFromServer(
                  new DeclinedReasonQueryParameters({ rooftopId: employee.rooftopId.toString() + ',-1' })
                ),
                new fromQuotes.GetQuotesFromServer({ vehicleId: a.params.id }),
              ];
              let transactionAction: any;
              let loadTransaction: any = null;
              // XS sends 0 for "no work order" / "no appointment"; only a positive id is a real transaction.
              if (Number(a.queryParams.workOrderId) > 0) {
                actions.push(
                  new fromVehicleMaintenance.SetTransactionFromQueryParams({
                    source: 'workOrder',
                    id: a.queryParams.workOrderId,
                  })
                );
                actions.push(
                  new fromWorkOrders.GetWorkOrderFromServer({
                    id: a.queryParams.workOrderId,
                    query: { embed: 'cases,declinedMpviCode' },
                  })
                );
              } else if (Number(a.queryParams.appointmentId) > 0) {
                actions.push(
                  new fromVehicleMaintenance.SetTransactionFromQueryParams({
                    source: 'appointmentId',
                    id: a.queryParams.appointmentId,
                  })
                );
                actions.push(
                  new fromAppointments.GetAppointmentFromServer({
                    id: a.queryParams.appointmentId,
                    query: { embed: 'cases,serviceAppointmentDeclinedMpviCode' },
                  })
                );
              } else {
                actions.push(
                  (transactionAction = new fromVehicleMaintenance.SetTransactionFromQueryParams({
                    source: 'vehicle',
                    id: a.params.id,
                  }))
                );
              }
              return of(actions).pipe(mergeAll());
            })
          );
        } else {
          return;
        }

        //return new fromConversations.GetConversationFromStateServer(a.params.id);
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadMenuFromQuoteRouter = createEffect(() =>
    this.dataPersistence.navigation(QuoteMenuComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
          filter((employee) => employee !== null),
          switchMap((employee: Employee) => {
            let params = new VehicleMaintenanceScheduleQueryParameters({ vehicleId: a.params.id });
            let params2 = new VehicleMaintenanceScheduleServiceDueQueryParameters({ vehicleId: a.params.id });
            let actions: Action[] = [
              new fromVehicles.GetVehiclesFromServer(new VehicleQueryParameters({ id: a.params.id })),
              new fromMaintenanceschedule.GetMaintenanceSchedulesFromServer(params),
              new fromServicesDue.GetServicesDueFromServer(params2),
              new fromServiceGroups.GetServiceGroupsFromServer(
                new ServiceGroupQueryParameters({ rooftopId: employee.rooftopId.toString() + ',-1' })
              ),
              new fromServices.GetServicesFromServer(new ServiceQueryParameters()),
              new fromServiceGroupRates.GetServiceGroupRatesFromServer(
                new ServiceGroupRateQueryParameters({ effectiveDate: new Date(), expiryDate: new Date() })
              ),
              new fromTemplateDetail.GetTemplateDetailsFromServer({
                params: new VehicleTemplateDetailQueryParameters(),
                vehicleId: a.params.id,
              }),
              new fromDeclinedReasons.GetDeclinedReasonsFromServer(
                new DeclinedReasonQueryParameters({ rooftopId: employee.rooftopId.toString() + ',-1' })
              ),
            ];
            let transactionAction: any;
            let loadTransaction: any = null;

            actions.push(
              new fromVehicleMaintenance.SetTransactionFromQueryParams({
                source: 'quote',
                id: a.params.quoteId,
              })
            );

            actions.push(
              new fromVehicleMaintenance.SetTransactionFromQueryParams({
                source: 'quote',
                id: a.params.quoteId,
              })
            );

            actions.push(
              new fromQuotes.GetQuoteFromServer({
                id: a.params.quoteId,
                query: null,
              })
            );

            return of(actions).pipe(mergeAll());
          })
        );

        //return new fromConversations.GetConversationFromStateServer(a.params.id);
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  postAppointmentCaseSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromAppointments.POST_APPOINTMENT_CASE_TO_SERVER_SUCCESS, {
      run: (action: fromAppointments.PostAppointmentCaseToServerSuccess, state: any) => {
        let actions = [];
        actions.push(
          new fromPendingAcceptedServices.RemovePendingAcceptedServiceInState({
            id: action.payload.case.serviceGroupId,
          })
        );

        actions.push(
          new fromAcceptedServices.AddAcceptedServiceInState({
            serviceGroup: state.vehicleMaintenance.pendingAcceptedServices.entities[action.payload.case.serviceGroupId],
          })
        );

        return of(actions).pipe(mergeAll());
      },
      onError: (action: fromAppointments.PostAppointmentCaseToServerSuccess, error) => {
        console.error('Error', error);
      },
    })
  );

  postWorkOrderCaseSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromWorkOrders.POST_WORK_ORDER_CASE_TO_SERVER_SUCCESS, {
      run: (action: fromWorkOrders.PostWorkOrderCaseToServerSuccess, state: any) => {
        let actions = [];
        actions.push(
          new fromPendingAcceptedServices.RemovePendingAcceptedServiceInState({
            id: action.payload.case.serviceGroupId,
          })
        );

        actions.push(
          new fromAcceptedServices.AddAcceptedServiceInState({
            serviceGroup: state.vehicleMaintenance.pendingAcceptedServices.entities[action.payload.case.serviceGroupId],
          })
        );

        return of(actions).pipe(mergeAll());
      },
      onError: (action: fromWorkOrders.PostWorkOrderCaseToServerSuccess, error) => {
        console.error('Error', error);
      },
    })
  );

  postAppointmentDeclinedServiceSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromAppointments.POST_APPOINTMENT_DECLINED_SERVICE_TO_SERVER_SUCCESS, {
      run: (action: fromAppointments.PostAppointmentDeclinedServiceToServerSuccess, state: any) => {
        let actions: Action[] = [];
        let serviceGroup;
        for (let i = 0; i <= state.vehicleMaintenance.pendingDeclinedServices.ids.length; i++) {
          if (
            state.vehicleMaintenance.pendingDeclinedServices.entities[
              state.vehicleMaintenance.pendingDeclinedServices.ids[i]
            ].id == action.payload.declinedService.opCode ||
            state.vehicleMaintenance.pendingDeclinedServices.entities[
              state.vehicleMaintenance.pendingDeclinedServices.ids[i]
            ].workOrderLocationOpCode == action.payload.declinedService.opCode
          ) {
            actions.push(
              new fromPendingDeclinedServices.RemovePendingDeclinedServiceInState({
                id: state.vehicleMaintenance.pendingDeclinedServices.entities[
                  state.vehicleMaintenance.pendingDeclinedServices.ids[i]
                ].id,
              })
            );
            actions.push(
              new fromDeclinedServices.AddDeclinedServiceInState({
                serviceGroup:
                  state.vehicleMaintenance.pendingDeclinedServices.entities[
                    state.vehicleMaintenance.pendingDeclinedServices.ids[i]
                  ],
              })
            );
            return of(actions).pipe(mergeAll());
          }
        }

        return of(actions).pipe(mergeAll());
      },
      onError: (action: fromAppointments.PostAppointmentDeclinedServiceToServerSuccess, error) => {
        console.error('Error', error);
      },
    })
  );

  postWorkOrderDeclinedServiceSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromWorkOrders.POST_DECLINED_SERVICE_TO_WORKORDER_TO_SERVER_SUCCESS, {
      run: (action: fromWorkOrders.PostDeclinedServiceToWorkOrderToServerSuccess, state: any) => {
        let actions: Action[] = [];
        let serviceGroup;
        for (let i = 0; i <= state.vehicleMaintenance.pendingDeclinedServices.ids.length; i++) {
          if (
            state.vehicleMaintenance.pendingDeclinedServices.entities[
              state.vehicleMaintenance.pendingDeclinedServices.ids[i]
            ].id == action.payload.declinedMpviCode.opCode ||
            state.vehicleMaintenance.pendingDeclinedServices.entities[
              state.vehicleMaintenance.pendingDeclinedServices.ids[i]
            ].workOrderLocationOpCode == action.payload.declinedMpviCode.opCode
          ) {
            actions.push(
              new fromPendingDeclinedServices.RemovePendingDeclinedServiceInState({
                id: state.vehicleMaintenance.pendingDeclinedServices.entities[
                  state.vehicleMaintenance.pendingDeclinedServices.ids[i]
                ].id,
              })
            );
            actions.push(
              new fromDeclinedServices.AddDeclinedServiceInState({
                serviceGroup:
                  state.vehicleMaintenance.pendingDeclinedServices.entities[
                    state.vehicleMaintenance.pendingDeclinedServices.ids[i]
                  ],
              })
            );
            return of(actions).pipe(mergeAll());
          }
        }

        return of(actions).pipe(mergeAll());
      },
      onError: (action: fromWorkOrders.PostDeclinedServiceToWorkOrderToServerSuccess, error) => {
        console.error('Error', error);
      },
    })
  );

  constructor(
    private actions: Actions,
    private dataPersistence: DataPersistence<VehicleMaintenanceState>,
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService
  ) {}
}
